if (!customElements.get('featured-product-variants')) {
  customElements.whenDefined('variant-selects').then(() => {
    if (customElements.get('featured-product-variants')) return;

    customElements.define('featured-product-variants', class extends customElements.get('variant-selects') {
      connectedCallback() {
        this.info = this.closest('product-info');
        this.updateMasterId();
        this.updateVariantStatuses();
        this.updatePaymentTerms();
        if (this.info?.querySelector('[name="add"]')?.disabled) {
          this.info.querySelectorAll('[data-forced-oos-dynamic-checkout], [data-forced-oos-purchase-options]').forEach((element) => { element.hidden = true; });
        }
        this.guardSubmission = (event) => {
          const id = new FormData(event.target).get('id');
          const button = event.target.querySelector('[name="add"]');
          if (this.pending || String(this.currentVariant?.id) !== String(id) || !this.isVariantPurchasable(this.currentVariant) || button?.disabled) {
            event.preventDefault();
            event.stopImmediatePropagation();
          }
        };
        this.info?.addEventListener('submit', this.guardSubmission, true);
      }

      disconnectedCallback() {
        this.request?.abort();
        this.info?.removeEventListener('submit', this.guardSubmission, true);
      }

      getVariantData() {
        this.variantData ||= JSON.parse(this.querySelector('[type="application/json"]').textContent);
        this.blockedVariants ||= JSON.parse(this.querySelector('[data-forced-oos-variants]')?.textContent || '{}');
        return this.variantData.map((variant) => ({
          ...variant,
          forced_out_of_stock: this.blockedVariants[variant.id] === true || window.ForcedOutOfStock?.isBlockedVariant(variant.id) === true,
        }));
      }

      updateMasterId() {
        const selected = this.querySelector('select[data-featured-variant], input[data-featured-variant]:checked');
        const id = selected?.value || this.dataset.selectedVariant;
        this.currentVariant = this.getVariantData().find((variant) => String(variant.id) === id);
      }

      updateVariantInput() {
        this.info.querySelectorAll('form input[name="id"]').forEach((input) => {
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
        this.updatePaymentTerms();
      }

      updatePaymentTerms() {
        if (!this.currentVariant) return;
        this.info.querySelectorAll('shopify-payment-terms').forEach((terms) => {
          terms.setAttribute('variant-id', String(this.currentVariant.id));
        });
      }

      updateVariantStatuses() {
        const variants = this.getVariantData();
        this.querySelectorAll('input[data-featured-variant], option').forEach((input) => {
          const variant = variants.find((item) => String(item.id) === input.value);
          const available = this.isVariantPurchasable(variant);
          if (input.tagName === 'OPTION') {
            input.textContent = available ? variant.title : window.variantStrings.unavailable_with_option.replace('[value]', variant.title);
          } else {
            input.classList.toggle('disabled', !available);
          }
        });
      }

      async onVariantChange(event) {
        if (!event.target.matches('[data-featured-variant]')) return;
        this.request?.abort();
        const request = new AbortController();
        this.request = request;
        this.pending = true;
        this.updateMasterId();
        this.updateVariantStatuses();
        this.toggleAddButton(true);
        this.info.querySelectorAll('[data-forced-oos-dynamic-checkout], [data-forced-oos-purchase-options]').forEach((element) => { element.hidden = true; });
        this.removeErrorMessage();
        this.info.setAttribute('aria-busy', 'true');

        try {
          if (!this.currentVariant) throw new Error('Invalid variant selection');
          const id = this.currentVariant.id;
          const url = new URL(this.dataset.url, window.location.origin);
          url.searchParams.set('variant', id);
          url.searchParams.set('section_id', this.dataset.section);
          const response = await fetch(url, { signal: request.signal });
          if (!response.ok) throw new Error('Unable to load variant');
          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          if (this.request !== request || !this.isConnected) return;
          const updatedInfo = html.getElementById(`ProductInfo-${this.dataset.section}`);
          if (updatedInfo?.dataset.selectedVariant !== String(id)) throw new Error('Variant response did not match');

          const updatedPicker = updatedInfo.querySelector('featured-product-variants');
          if (updatedPicker) {
            this.variantData = JSON.parse(updatedPicker.querySelector('[type="application/json"]').textContent);
            this.blockedVariants = JSON.parse(updatedPicker.querySelector('[data-forced-oos-variants]')?.textContent || '{}');
            this.updateMasterId();
            this.updateVariantStatuses();
          }
          for (const prefix of ['price', 'Sku', 'Inventory', 'Volume', 'Price-Per-Item', 'Volume-Note']) {
            const current = document.getElementById(`${prefix}-${this.dataset.section}`);
            const updated = html.getElementById(`${prefix}-${this.dataset.section}`);
            if (!current || !updated) continue;
            current.innerHTML = updated.innerHTML;
            current.className = updated.className;
            if (updated.dataset.variantId) current.dataset.variantId = updated.dataset.variantId;
          }

          this.updateMedia(html);
          this.updateVariantInput();
          this.updateShareUrl();
          this.info.dataset.selectedVariant = String(id);
          const button = this.info.querySelector('[name="add"]');
          const updatedButton = updatedInfo.querySelector('[name="add"]');
          this.info.querySelectorAll('[data-forced-oos-dynamic-checkout], [data-forced-oos-purchase-options]').forEach((element) => {
            const selector = element.hasAttribute('data-forced-oos-dynamic-checkout') ? '[data-forced-oos-dynamic-checkout]' : '[data-forced-oos-purchase-options]';
            element.hidden = updatedInfo.querySelector(selector)?.hidden !== false || updatedButton?.disabled === true;
          });
          if (button && updatedButton) {
            button.disabled = updatedButton.disabled;
            button.removeAttribute('aria-disabled');
            button.querySelector('span').textContent = updatedButton.querySelector('span').textContent;
          }
          publish(PUB_SUB_EVENTS.variantChange, { data: { sectionId: this.dataset.section, html, variant: this.currentVariant } });
        } catch (error) {
          if (error.name === 'AbortError' || this.request !== request) return;
          this.toggleAddButton(true, window.variantStrings.unavailable);
          this.info.querySelector('product-form')?.handleErrorMessage(this.dataset.errorMessage);
        } finally {
          if (this.request === request) {
            this.pending = false;
            this.info.removeAttribute('aria-busy');
          }
        }
      }

      updateMedia(html) {
        super.updateMedia(html);
        const image = document.getElementById(`FeaturedImage-${this.dataset.section}`);
        const updated = html.getElementById(`FeaturedImage-${this.dataset.section}`);
        if (!image || !updated) return;
        for (const attribute of ['src', 'srcset', 'sizes', 'alt', 'onclick']) {
          if (updated.hasAttribute(attribute)) image.setAttribute(attribute, updated.getAttribute(attribute));
          else image.removeAttribute(attribute);
        }
        const updatedButtons = updated.closest('section').querySelectorAll('.thumbnail-btn');
        this.info.closest('section').querySelectorAll('.thumbnail-btn').forEach((button, index) => {
          if (!updatedButtons[index]) return;
          button.classList.toggle('active', updatedButtons[index].classList.contains('active'));
          button.style.borderColor = updatedButtons[index].style.borderColor;
        });
      }
    });
  });
}
